import { isCurrentlyValid } from "@/lib/sort-utils"
import moment from "moment"
import {
  CREATE_BILLING_PLAN_CONTRACT_ASSOCIATION,
  FETCH_BILLING_PLAN_CONTRACT_ASSOCIATION,
  FETCH_CONTRACT_CUSTOMER_DATA,
  FETCH_CONTRACTS,
  FETCH_CONTRACT_PERSON_DATA,
  UPDATE_BILLING_PLAN_CONTRACT_ASSOCIATION,
  UPDATE_CONTRACT, FETCH_CONTRACT_RELATED_FILES, FETCH_GENERATED_CONTRACT_BUNDLES, REGENERATE_CONTRACT,
  GENERATE_CONTRACT, UPLOAD_CONTRACT_RELATED_FILE
} from "@/store/action-types"
import { cachedActionWrapper, loadingStateWrapper } from "@/modules/common/store/tools"
import { INVALIDATE_CACHE } from "@/modules/common/store/action-types"
import {
  SET_CONTRACT_CUSTOMER_DATA,
  SET_CONTRACT_PERSON_DATA,
  SET_CONTRACT_RELATED_FILES,
  SET_CONTRACTS, SET_GENERATED_CONTRACT_BUNDLES
} from "@/store/mutation-types"
import {
  CACHE_CONTRACT_CUSTOMER_DATA,
  CACHE_CONTRACT_PERSON_DATA,
  CACHE_CONTRACT_RELATED_FILES,
  CACHE_CONTRACTS, CACHE_GENERATED_CONTRACT_BUNDLES
} from "@/store/cache-types"
import { SET_ERROR_MESSAGE, SET_SNACKBAR_MESSAGE } from "@/modules/common/store/mutation-types"
import { CONTRACT_PRODUCT_CLASSES, ECOM_DEVICE_LIST } from "@/lib/device-db"

export const CONTRACT_SCOPE_KNB = 'knb'
export const CONTRACT_SCOPE_ECOMMERCE = 'ecommerce'

export const RELATED_FILE_ATTACHMENT_IDENTIFIER_DEVICES = "7"
export const RELATED_FILE_ATTACHMENT_IDENTIFIER_ECOM = "8"

export default {
  namespaced: true,
  state: () => {
    return {
      contracts: [],
      contractCustomerData: [],
      contractPersonData: [],
      contractRelatedFiles: [],
      generatedContractBundles: [],
    }
  },
  getters: {
    contractsById (state) {
      const result = {}

      state.contracts.forEach(contract => {
        result[contract.id] = contract
      })
      return result
    },
    contractCustomerDataById (state) {
      const result = {}

      state.contractCustomerData.forEach(entry => {
        result[entry.id] = entry
      })

      return result
    },
    contractRelatedFilesByCustomer (state) {
      const result = {}

      state.contractRelatedFiles.forEach(entry => {
        if (!(entry.customer in result)) {
          result[entry.customer] = []
        }

        result[entry.customer].push(entry)
      })

      return result
    },
    generatedContractBundlesByCustomer (state) {
      const result = {}

      state.generatedContractBundles.forEach(entry => {
        if (!(entry.customer in result)) {
          result[entry.customer] = []
        }

        result[entry.customer].push(entry)
      })

      return result
    },
    contractCustomerData (state, getters) {
      return state.contractCustomerData.map((entry) => {
        entry.organizationId = entry.organization ? entry.organization.split('/').filter(segment => !!segment).pop() : null

        const relevantPersonNames = []
        const relevantPeopleUrls = []
        const relevantPeopleLegitimizationStatus = []

        entry.persons.forEach(personUrl => {
          const person = getters.contractPersonDataByUrl[personUrl]
          if (!person) {
            return
          }

          if (!person.types.includes('authorized_signer')) {
            return
          }

          const name = [
            person?.first_name ?? '',
            person?.last_name ?? ''
          ].join(' ')

          relevantPersonNames.push(name)
          relevantPeopleUrls.push(personUrl)
          relevantPeopleLegitimizationStatus.push(person.legitimization_status || 'unknown')
        })
        entry.relevantPeople = relevantPersonNames
        entry.relevantPeopleUrls = relevantPeopleUrls
        entry.relevantPeopleLegitimizationStatus = relevantPeopleLegitimizationStatus

        const deviceMap = {}
        const ecomMap = {}

        Object.keys(entry.products_requested).forEach(deviceClass => {
          const device = deviceClass.toUpperCase()
          const count = entry.products_requested[deviceClass]
          if (ECOM_DEVICE_LIST.includes(device)) {
            ecomMap[device] = (ecomMap[device] || 0) + count
          } else {
            deviceMap[device] = (deviceMap[device] || 0) + count
          }
        })

        entry.requestedDeviceProducts = Object.entries(deviceMap).map(([device, count]) => ({
          deviceClass: device, count, label: CONTRACT_PRODUCT_CLASSES[device],
        }))
        entry.requestedEcomProducts = Object.entries(ecomMap).map(([device, count]) => ({
          deviceClass: device, count, label: CONTRACT_PRODUCT_CLASSES[device],
        }))

        entry.relatedFileDevices = null
        entry.relatedFileEcom = null

        const relatedFiles = getters.contractRelatedFilesByCustomer[entry.url] ?? []
        relatedFiles.forEach(relatedFile => {
          if (relatedFile.attachment_number === RELATED_FILE_ATTACHMENT_IDENTIFIER_DEVICES) {
            entry.relatedFileDevices = relatedFile
          }

          if (relatedFile.attachment_number === RELATED_FILE_ATTACHMENT_IDENTIFIER_ECOM) {
            entry.relatedFileEcom = relatedFile
          }
        })

        const generatedContractBundlesSorted = (getters.generatedContractBundlesByCustomer[entry.url] ?? []).sort((bundleA, bundleB) => {
          return new Date(bundleB.created_at) - new Date(bundleA.created_at)
        })

        entry.generatedContractBundle = generatedContractBundlesSorted.length ? generatedContractBundlesSorted[0] : null
        entry.isContractRegenerating = false

        return entry
      })
    },
    contractPersonDataByUrl (state) {
      const result = {}
      state.contractPersonData.forEach(entry => {
        result[entry.url] = entry
      })
      return result
    },
    getContractPersonByUrl: (_state, getters) => (url) => {
      return getters.contractPersonDataByUrl[url]
    },
    contractsByOrganization: (state) => {
      const result = {}
      state.contracts.forEach(contract => {
        if (!result[contract.organization]) {
          result[contract.organization] = {
            allContracts: [],
            validContracts: [],
            hasValidContract: false
          }
        }
        result[contract.organization].allContracts.push(contract)
        if (isCurrentlyValid(contract)) {
          result[contract.organization].validContracts.push(contract)
          result[contract.organization].hasValidContract = true
        }
      })

      return result
    },
    contractsByOrganizationAndDevices: (_state, getters, rootState) => {
      const result = {}
      rootState.device.devices.forEach(device => {
        if (!device.contracts.length) {
          return
        }

        device.contracts.forEach(deviceContract => {
          if (!result[deviceContract.contract.organization]) {
            result[deviceContract.contract.organization] = {
              allContracts: [],
              validContracts: [],
              hasValidContract: false,
              deviceClasses: []
            }
          }
          const contract = getters.contractsById[deviceContract.contract.id]
          result[deviceContract.contract.organization].deviceClasses.push(device.device_class)

          const contractFound = result[deviceContract.contract.organization].allContracts.find(c => c.id === contract.id)
          if (contractFound) {
            return
          }

          result[deviceContract.contract.organization].allContracts.push(contract)

          if (isCurrentlyValid(contract)) {
            result[deviceContract.contract.organization].validContracts.push(contract)
            result[deviceContract.contract.organization].hasValidContract = true
          }
        })
      })
      return result
    }
  },
  mutations: {
    [SET_CONTRACTS]: (state, { data }) => {
      state.contracts = data
    },
    [SET_CONTRACT_CUSTOMER_DATA]: (state, { data }) => {
      state.contractCustomerData = data
    },
    [SET_CONTRACT_PERSON_DATA]: (state, { data }) => {
      state.contractPersonData = data
    },
    [SET_CONTRACT_RELATED_FILES]: (state, { data }) => {
      state.contractRelatedFiles = data
    },
    [SET_GENERATED_CONTRACT_BUNDLES]: (state, { data }) => {
      state.generatedContractBundles = data
    },
  },
  actions: {
    [FETCH_CONTRACTS]: async (context) => {
      return cachedActionWrapper(context, CACHE_CONTRACTS, async () => {
        const response = await context.rootGetters.restApi.get('/contract/')
        context.commit(SET_CONTRACTS, { data: response.data })
      }, async (context) => {
        context.commit(SET_CONTRACTS, { data: [] })
      })
    },
    [FETCH_CONTRACT_CUSTOMER_DATA]: async (context) => {
      return cachedActionWrapper(context, CACHE_CONTRACT_CUSTOMER_DATA, async () => {
        const response = await context.rootGetters.restApi.get('/contract/application/customer')
        context.commit(SET_CONTRACT_CUSTOMER_DATA, { data: response.data })
      }, async (context) => {
        context.commit(SET_CONTRACT_CUSTOMER_DATA, { data: [] })
      })
    },
    [FETCH_CONTRACT_PERSON_DATA]: async (context) => {
      return cachedActionWrapper(context, CACHE_CONTRACT_PERSON_DATA, async () => {
        const response = await context.rootGetters.restApi.get('/contract/application/person')
        context.commit(SET_CONTRACT_PERSON_DATA, { data: response.data })
      }, async (context) => {
        context.commit(SET_CONTRACT_PERSON_DATA, { data: [] })
      })
    },
    [FETCH_CONTRACT_RELATED_FILES]: async (context) => {
      return cachedActionWrapper(context, CACHE_CONTRACT_RELATED_FILES, async () => {
        const response = await context.rootGetters.restApi.get('/contract/application/contract_related_file/')
        context.commit(SET_CONTRACT_RELATED_FILES, { data: response.data })
      }, async (context) => {
        context.commit(SET_CONTRACT_RELATED_FILES, { data: [] })
      })
    },
    [FETCH_GENERATED_CONTRACT_BUNDLES]: async (context) => {
      return cachedActionWrapper(context, CACHE_GENERATED_CONTRACT_BUNDLES, async () => {
        const response = await context.rootGetters.restApi.get('/contract/application/generated_contract_bundle/')
        context.commit(SET_GENERATED_CONTRACT_BUNDLES, { data: response.data })
      }, async (context) => {
        context.commit(SET_GENERATED_CONTRACT_BUNDLES, { data: [] })
      })
    },
    [FETCH_BILLING_PLAN_CONTRACT_ASSOCIATION]: async (context, { contractId }) => {
      return loadingStateWrapper(context, async () => {
        const response = await context.rootGetters.restApi.get(`/contract/${contractId}/billing/`)
        return response.data
      }, async (context) => {
        return false
      })
    },
    [CREATE_BILLING_PLAN_CONTRACT_ASSOCIATION]: async (context, {
      contract,
      billingPlanUrl,
      validFrom,
      validNotafter
    }) => {
      const formattedValidFrom = validFrom ? (moment.isMoment(validFrom) ? validFrom.toISOString() : moment(validFrom).toISOString()) : context.rootState.now.toISOString()
      const formattedValidNotafter = validNotafter ? (moment.isMoment(validNotafter) ? validNotafter.toISOString() : moment(validNotafter).toISOString()) : null

      return loadingStateWrapper(context, async () => {
        const payload = {
          billing_plan: billingPlanUrl,
          valid_from: formattedValidFrom,
        }
        if (formattedValidNotafter !== null) {
          payload.valid_notafter = formattedValidNotafter
        }
        const response = await context.rootGetters.restApi.post(`/contract/${contract.id}/billing/`, payload)
        context.commit(SET_SNACKBAR_MESSAGE, { message: 'Rechnungskonfiguration erstellt' }, { root: true })
        return response.data
      }, async (context) => {
        return false
      })
    },
    [UPDATE_BILLING_PLAN_CONTRACT_ASSOCIATION]: async (context, {
      contractId,
      billingPlanUrl,
      associationId,
      validFrom,
      validNotafter
    }) => {
      const formattedValidFrom = validFrom ? (moment.isMoment(validFrom) ? validFrom.toISOString() : validFrom) : null
      const formattedValidNotafter = validNotafter ? (moment.isMoment(validNotafter) ? validNotafter.toISOString() : validNotafter) : null

      return loadingStateWrapper(context, async () => {
        const payload = {
          id: associationId,
          billing_plan: billingPlanUrl,
        }
        if (formattedValidFrom !== null) {
          payload.valid_from = formattedValidFrom
        }
        if (formattedValidNotafter !== null) {
          payload.valid_notafter = formattedValidNotafter
        }
        const response = await context.rootGetters.restApi.patch(`/contract/${contractId}/billing/`, payload)
        context.commit(SET_SNACKBAR_MESSAGE, { message: 'Rechnungskonfiguration aktualisiert' }, { root: true })
        return response.data
      }, async (context) => {
        return false
      })
    },
    [UPDATE_CONTRACT]: async (context, {
      contract,
      sepaDdActive
    }) => {
      return loadingStateWrapper(context, async () => {
        const response = await context.rootGetters.restApi.patch(contract.url, {
          sepadd_active: sepaDdActive
        })
        context.commit(SET_SNACKBAR_MESSAGE, { message: 'Vertrag aktualisiert' }, { root: true })
        return response.data
      }, async (context) => {
        return false
      })
    },
    [GENERATE_CONTRACT]: async (context, {
      customerUrl
    }) => {
      return loadingStateWrapper(context, async () => {
        await context.rootGetters.restApi.post(`${customerUrl}generate_contract/`)
        context.commit(SET_SNACKBAR_MESSAGE, { message: 'Vertrag wird generiert. Dies kann einige Minuten dauern.' }, { root: true })
      }, async (error) => {
        const errorMessage = error?.response?.data.join('; ') || 'Fehler beim Generieren des Vertrags'
        context.commit(SET_ERROR_MESSAGE, { message: errorMessage }, { root: true })
        return false
      })
    },
    [REGENERATE_CONTRACT]: async (context, {
      customerUrl
    }) => {
      return loadingStateWrapper(context, async () => {
        await context.rootGetters.restApi.post(`${customerUrl}re_generate_contract/`)
        context.commit(SET_SNACKBAR_MESSAGE, { message: 'Vertrag wird regeneriert. Dies kann einige Minuten dauern.' }, { root: true })
      }, async (error) => {
        const errorMessage = error?.response?.data.join('; ') || 'Fehler beim Regenerieren des Vertrags'
        context.commit(SET_ERROR_MESSAGE, { message: errorMessage }, { root: true })
        return false
      })
    },
    [UPLOAD_CONTRACT_RELATED_FILE]: async (context, {
      file,
      name,
      attachmentNumber,
      customerUrl
    }) => {
      return loadingStateWrapper(context, async () => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('name', name)
        formData.append('attachment_number', attachmentNumber)

        await context.rootGetters.restApi.post(`${customerUrl}upload_contract_related_file/`, formData)

        await context.dispatch(INVALIDATE_CACHE, { key: CACHE_CONTRACT_RELATED_FILES }, { root: true })
        await context.dispatch(FETCH_CONTRACT_RELATED_FILES)

        context.commit(SET_SNACKBAR_MESSAGE, { message: 'Datei erfolgreich hochgeladen' }, { root: true })
        return true
      }, async (error) => {
        const errorMessage = error?.response?.data?.detail || error?.message || 'Fehler beim Hochladen der Datei'
        context.commit(SET_ERROR_MESSAGE, { message: errorMessage }, { root: true })
        return false
      })
    },
  }
}
